import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CallGetCategories, CallPostCategories } from '@library/request/callProduct';
import CommonSwitchButton from '@components/molecules/button/SwitchButton';
import Spinner from '@components/molecules/Spinner';
import { CategoryDetail, PRODUCT_KEYS } from '@type/productType';
import { useMenuPermission } from '@src/recoil/permission';

const initCategory = {
  categoryName: '',
  description: '',
  categoryFold: 0, // 1: 접힘, 0: 펼침
  displayType: '1', // 노출 채널 (1: 전체, 2: 위메프오, 3: 자사앱)
  beginDate: new Date().toISOString().split('T')[0], // 노출 시작 날짜
  endDate: '9999-12-31', // 노출 종료 닐짜
  beginTime: '00:00:00', // 노출 시작 시간
  endTime: '00:00:00', // 노출 시작 시간
  day: [
    { label: '월', value: 1 },
    { label: '화', value: 1 },
    { label: '수', value: 1 },
    { label: '목', value: 1 },
    { label: '금', value: 1 },
    { label: '토', value: 1 },
    { label: '일', value: 1 },
  ], // 노출 요일 요일
};

// 카테고리 추가/정보수정
const CategoryAdd = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const storeId = searchParams.get('storeID');
  const categoryID = searchParams.get('categoryID');

  const [loading, setLoading] = useState(false);
  const [category, setCategory] = useState<CategoryDetail>(); // 현재 카테고리
  const [isDirty, setIsDirty] = useState(false); // 수정 여부

  const { isNoAuth1, isNoAuth2 } = useMenuPermission(storeId);

  const isEdit = !!categoryID;
  const isBtnDisabled = useMemo(() => {
    if (isEdit) return isNoAuth2; // 수정: isNoAuth2
    return isNoAuth1; // 추가: isNoAuth1
  }, [isEdit, isNoAuth1, isNoAuth2]);

  // 카테고리 리스트 조회 API
  const { data: categoryList } = useQuery<any[]>([PRODUCT_KEYS.CATEGORY, storeId], {
    enabled: !!storeId,
    queryFn: async () => {
      try {
        setLoading(true);
        const response = await CallGetCategories(storeId);
        if (response?.result.code !== 1) return;
        const { data } = response;
        return data;
      } catch (error) {
        console.log('error', error);
        throw error;
      } finally {
        setLoading(false);
      }
    },
    select: (data) =>
      data.map((item) => {
        if (item.categoryCondition) {
          const { categoryCondition, ...rest } = item;
          return { ...rest, ...categoryCondition };
        }
        return item;
      }),
  });

  // 카테고리 수정/추가 API
  const { mutate: categoryMutate } = useMutation(
    (param: { categories: CategoryDetail[] }) => CallPostCategories(storeId, param),
    {
      onMutate: () => {
        setLoading(true);
      },
      onSuccess: (response) => {
        const msg = categoryID ? '정보 수정이' : '추가가';
        const { result } = response;
        if (result.code !== 1) {
          alert(result.message);
        } else {
          window.alert(`카테고리 ${msg} 완료되었습니다.`);
          queryClient.invalidateQueries([PRODUCT_KEYS.CATEGORY, storeId]); // 카테고리 리스트 재조회
          // navigate(`/product/list?tab=menu&storeID=${storeId}`);
          window.history.back();
        }
      },
      onError: (error) => {
        console.log('error:', error);
      },
      onSettled: () => {
        setLoading(false);
      },
    },
  );

  const onChange = (e: any) => {
    let { id, value } = e.target;
    if (id === 'categoryName') {
      value = value.slice(0, 100);
    }

    setCategory((prev) => ({ ...prev, [id]: value }));
    setIsDirty(true);
  };

  const onClickSave = () => {
    if (!category.categoryName) return;

    let data;
    if (categoryID) {
      // 수정
      data = categoryList.map((list) => (list.categoryID === categoryID ? { ...category } : list));
    } else {
      data = categoryList.concat(category); // 추가
    }
    categoryMutate({ categories: data });
  };

  useEffect(() => {
    if (!categoryList) return;

    if (categoryID) {
      // 카테고리 정보 수정 (edit)
      const currentData = categoryList.filter((list) => list.categoryID === categoryID);
      setCategory(currentData[0]);
    } else {
      // 카테고리 추가 (new)
      setCategory(initCategory);
    }
  }, [categoryList, categoryID]);

  return (
    <article id="mArticle" className="art-product">
      <div className="view-product">
        <div className="box-product">
          <strong className="lab-product">
            카테고리명<span className="mark-ess">*</span>
          </strong>
          <input
            type="text"
            id="categoryName"
            className="tf-cont"
            placeholder="예)프리미엄 피자"
            autoComplete="off"
            data-string="text"
            value={category?.categoryName || ''}
            onChange={onChange}
            maxLength={100}
            disabled={isBtnDisabled}
          />
        </div>
        <div className="box-product">
          <strong className="lab-product">한줄 설명</strong>
          <textarea
            id="description"
            className="tf-cont"
            placeholder="예)가장 인기 있는 베스트 메뉴 모음입니다."
            data-string="text"
            value={category?.description || ''}
            onChange={onChange}
            disabled={isBtnDisabled}
          />
        </div>
        <div className="box-product">
          <strong className="lab-product">카테고리 펼침</strong>
          <CommonSwitchButton
            id="categoryFold"
            check={category?.categoryFold === 0}
            onChange={(e) => {
              setCategory((prev) => ({ ...prev, categoryFold: e.target.checked ? 0 : 1 }));
              setIsDirty(true);
            }}
          />
          <p className="switch-help">위메프오 앱에서 메뉴 목록을 펼침/접힘할 수 있는 기능입니다.</p>
        </div>
        <div className="fixedBtnGroup">
          <button
            type="button"
            id="btnSubmit"
            className="btnDone"
            disabled={!category?.categoryName || !isDirty || isBtnDisabled}
            onClick={onClickSave}
          >
            저장
          </button>
        </div>
      </div>
      <Spinner show={loading} />
    </article>
  );
};

export default CategoryAdd;
